import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Remove as RemoveIcon,
  ShoppingCart as ShoppingCartIcon,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link as RouterLink, useNavigate } from "react-router-dom";

import { amazonColors } from "../../../config/amazonTheme";
import { ROUTES } from "../../../config/constants";
import { useExchangeRate } from "../../../hooks/useExchangeRate";
import { usePublishTopOffset } from "../../../hooks/usePublishTopOffset";
import { convertUsdToTjs } from "../../../services/exchangeRateService";
import type { CartItem } from "../../../store/useCartStore";
import { useCartStore } from "../../../store/useCartStore";
import { trackInitiateCheckout } from "../../../utils/metaPixel";
import Price from "../../common/Price";
import ShippingInfoCard from "../../common/ShippingInfoCard";

const Cart: React.FC = () => {
  const navigate = useNavigate();
  const { t: myTranslate } = useTranslation();
  const {
    items,
    removeFromCart,
    updateQuantity,
    getItemCount,
    getTotalPrice,
    getAdjustedShippingCost,
    calculateMissingShipping,
    isCalculatingShipping,
    hasItemsNeedingShipping,
    getItemsNeedingShipping,
  } = useCartStore();

  usePublishTopOffset();
  const { data: exchangeRate } = useExchangeRate();

  // Calculate shipping ONCE when page loads (if needed)
  useEffect(() => {
    const itemsNeedingShipping = getItemsNeedingShipping();
    if (itemsNeedingShipping.length > 0 && !isCalculatingShipping) {
      calculateMissingShipping();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty deps - run ONCE on mount

  const handleQuantityChange = (productId: string, newQuantity: number) => {
    if (newQuantity >= 1 && newQuantity <= 99) {
      updateQuantity(productId, newQuantity);
    }
  };

  const handleRemoveItem = (productId: string) => {
    removeFromCart(productId);
  };

  const getCartItemProductHref = (item: CartItem) => {
    const retailer = item.product.retailer || "amazon";
    return `/product/${item.product.product_id}?retailer=${retailer}`;
  };

  const productLinkSx = {
    color: "inherit",
    textDecoration: "none",
    transition: "color 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease",
    "&:hover": {
      color: amazonColors.blue,
    },
    "&:focus-visible": {
      outline: "none",
      boxShadow: `0 0 0 2px ${amazonColors.blue}`,
    },
  };

  const handleProceedToCheckout = () => {
    trackInitiateCheckout({
      value: totalPrice,
      currency: "USD",
      numItems: itemCount,
      contentIds: items.map((item) => item.product.product_id),
      contents: items.map((item) => ({
        id: item.product.product_id,
        quantity: item.quantity,
        item_price: Number(item.product.price) || 0,
      })),
    });

    navigate(ROUTES.CHECKOUT);
  };

  const renderShippingInfo = (item: CartItem) => {
    const parts: React.ReactNode[] = [];

    // Seller's internal shipping fee (e.g. $5.66 from Amazon seller)
    if (item.offerShippingFee && item.offerShippingFee > 0) {
      parts.push(
        <Typography
          key="seller-ship"
          variant="caption"
          className="notranslate"
          translate="no"
          component="span"
          sx={{ color: amazonColors.text.secondary, fontSize: "0.75rem" }}
        >
          +<Price value={item.offerShippingFee} tjsAmount={convertUsdToTjs(item.offerShippingFee, exchangeRate ?? null)} /> {myTranslate("cart.sellerShipping")}
        </Typography>
      );
    }

    // Cargo shipping (US→TJ)
    if (item.shippingCalculated && item.shippingCost !== undefined) {
      parts.push(
        <Typography
          key="cargo-ship"
          variant="caption"
          className="notranslate"
          translate="no"
          component="span"
          sx={{ color: amazonColors.text.secondary, fontSize: "0.75rem" }}
        >
          +<Price value={item.shippingCost} tjsAmount={convertUsdToTjs(item.shippingCost, exchangeRate ?? null)} /> shipping
        </Typography>
      );
    } else if (isCalculatingShipping && !item.shippingCalculated) {
      parts.push(
        <Box key="calc-ship" sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <CircularProgress size={12} />
          <Typography
            variant="caption"
            className="notranslate"
            translate="no"
            sx={{ color: amazonColors.text.secondary, fontSize: "0.75rem" }}
          >
            {myTranslate("cart.calculatingShipping")}
          </Typography>
        </Box>
      );
    } else if (item.product.ship_price) {
      parts.push(
        <Typography
          key="product-ship"
          variant="caption"
          className="notranslate"
          translate="no"
          component="span"
          sx={{ color: amazonColors.text.secondary, fontSize: "0.75rem" }}
        >
          +<Price value={item.product.ship_price} tjsAmount={convertUsdToTjs(Number(item.product.ship_price) || 0, exchangeRate ?? null)} /> shipping
        </Typography>
      );
    }

    return parts.length > 0 ? <>{parts}</> : null;
  };

  const itemCount = getItemCount();
  const totalPrice = getTotalPrice();
  const shippingResult = getAdjustedShippingCost();
  const itemCountLabel =
    itemCount === 1
      ? myTranslate("orders.itemsCount", { count: itemCount })
      : myTranslate("orders.itemsCountPlural", { count: itemCount });

  const checkoutSummary = (
    <>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "baseline",
          gap: 1,
        }}
      >
        <Typography
          variant="body2"
          className="notranslate"
          translate="no"
          sx={{ color: amazonColors.text.secondary, fontWeight: 700 }}
        >
          {myTranslate("common.subtotal")}
          <Box
            component="span"
            sx={{ ml: 0.5, color: amazonColors.text.secondary }}
          >
            ({itemCountLabel})
          </Box>
        </Typography>
        <Typography variant="h6" translate="no" sx={{ fontWeight: 800 }}>
          <Price value={totalPrice} disableCentsHeuristic tjsAmount={convertUsdToTjs(totalPrice, exchangeRate ?? null)} />
        </Typography>
      </Box>

      <Button
        variant="contained"
        fullWidth
        size="large"
        className="notranslate"
        translate="no"
        onClick={handleProceedToCheckout}
        disabled={items.length === 0 || isCalculatingShipping}
        sx={{
          backgroundColor: amazonColors.orange,
          color: "#fff",
          fontWeight: 800,
          borderRadius: 2,
          py: 1.1,
          textTransform: "none",
          border: "1px solid #f0b800",
          boxShadow: "inset 0 -2px 0 rgba(0,0,0,0.12)",
          "&:hover": { backgroundColor: amazonColors.orangeHover },
          "&:disabled": {
            backgroundColor: amazonColors.gray[300],
            color: amazonColors.text.secondary,
            boxShadow: "none",
            borderColor: "transparent",
          },
        }}
      >
        <Box
          component="span"
          sx={{
            display: "inline-flex",
            alignItems: "baseline",
            gap: 0.75,
          }}
        >
          <Box component="span">{myTranslate("cart.proceedToCheckout")}</Box>
        </Box>
      </Button>
    </>
  );

  const shippingInfoCard = (
    <ShippingInfoCard
      shippingCost={shippingResult.originalAmount}
      isCalculating={isCalculatingShipping}
      applyMinimumFee
    />
  );

  const summaryCard = (
    <Card
      sx={{
        borderRadius: 3,
        border: "1px solid #f0e7d8",
        backgroundColor: "#ffffff",
        boxShadow: "0 8px 20px rgba(0,0,0,0.06)",
      }}
    >
      <CardContent sx={{ p: { xs: 1.75, sm: 2 }, "&:last-child": { pb: 2 } }}>
        <Stack spacing={1.5}>
          {checkoutSummary}
          {shippingInfoCard}
        </Stack>
      </CardContent>
    </Card>
  );

  const stickyCheckoutCard = (
    <Card
      sx={{
        borderRadius: 3,
        border: "1px solid #f0e7d8",
        backgroundColor: "#ffffff",
        boxShadow: "0 8px 20px rgba(0,0,0,0.06)",
      }}
    >
      <CardContent sx={{ p: { xs: 1.5, sm: 2 }, "&:last-child": { pb: 2 } }}>
        <Stack spacing={1.1}>{checkoutSummary}</Stack>
      </CardContent>
    </Card>
  );

  if (items.length === 0) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          px: 2,
          py: 4,
          backgroundColor: "#f5f5f5",
        }}
      >
        <Box sx={{ textAlign: "center" }}>
          <ShoppingCartIcon
            sx={{ fontSize: 64, color: amazonColors.gray[400], mb: 2 }}
          />
          <Typography
            variant="h5"
            className="notranslate"
            translate="no"
            sx={{ mb: 2, color: amazonColors.text.primary }}
          >
            {myTranslate("cart.yourCartIsEmpty")}
          </Typography>
          <Typography
            variant="body1"
            className="notranslate"
            translate="no"
            sx={{ mb: 3, color: amazonColors.text.secondary }}
          >
            {myTranslate("cart.addSomeProducts")}
          </Typography>
          <Button
            variant="contained"
            className="notranslate"
            translate="no"
            onClick={() => navigate(ROUTES.HOME)}
            sx={{
              backgroundColor: amazonColors.yellow,
              color: amazonColors.text.primary,
              "&:hover": { backgroundColor: amazonColors.yellowHover },
            }}
          >
            {myTranslate("cart.continueShopping")}
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        px: { xs: 1.5, sm: 2 },
        pt: { xs: 0, md: 3 },
        pb: { xs: 2, md: 3 },
        backgroundColor: "#f5f5f5",
      }}
    >
      {/* CONTENT CONTAINER */}
      <Box
        sx={{
          width: "100%",
          maxWidth: 1200,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* HEADER (non-scrollable on desktop; natural on mobile) */}
        <Box sx={{ pt: { xs: 0, md: 0.5 }, pb: { xs: 0, md: 0.5 } }}>
          <Typography
            variant="h4"
            className="notranslate"
            translate="no"
            sx={{ mb: { xs: 1.5, md: 2 }, color: amazonColors.text.primary, display: { xs: "none", md: "block" } }}
          >
            {myTranslate("cart.shoppingCart", { count: itemCount })}
          </Typography>

          {hasItemsNeedingShipping() && !isCalculatingShipping && (
            <Alert
              severity="info"
              className="notranslate"
              translate="no"
              sx={{ mb: { xs: 1.5, md: 2 } }}
            >
              {myTranslate("cart.someItemsNeedShipping")}
            </Alert>
          )}
        </Box>

        <Box
          sx={{
            position: "sticky",
            top: "var(--app-top-offset)",
            zIndex: 6,
            display: { xs: "block", md: "none" },
            pb: 0,
            mb: 1.5,
          }}
        >
          {stickyCheckoutCard}
        </Box>

        <Box sx={{ display: { xs: "block", md: "none" }, mb: 1.5 }}>
          {shippingInfoCard}
        </Box>

        {/* BODY */}
        <Box
          sx={{
            display: "flex",
            gap: { xs: 2, md: 3 },
            flexDirection: { xs: "column", md: "row" },
          }}
        >
          {/* ITEMS */}
          <Box
            sx={{
              flex: 1,
              pr: { md: 1 },
            }}
          >
            <Card
              sx={{
                borderRadius: 3,
                border: "1px solid #E0E0E0",
                boxShadow: "0 8px 20px rgba(0,0,0,0.06)",
                backgroundColor: "#ffffff",
                overflow: "hidden",
              }}
            >
              {items.map((item, index) => {
                const productHref = getCartItemProductHref(item);

                return (
                  <Box key={item.cartItemId}>
                    <Box
                      sx={{
                        p: { xs: 1.5, sm: 2 },
                        display: "flex",
                        gap: { xs: 1.5, sm: 2 },
                        flexDirection: "row",
                        alignItems: "flex-start",
                      }}
                    >
                      <Box
                        component={RouterLink}
                        to={productHref}
                        aria-label={item.product.title}
                        sx={{
                          width: { xs: 72, sm: 84 },
                          height: { xs: 72, sm: 84 },
                          flexShrink: 0,
                          borderRadius: 2,
                          border: "1px solid #F5F5F5",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          bgcolor: "#ffffff",
                          overflow: "hidden",
                          ...productLinkSx,
                          "&:hover": {
                            ...productLinkSx["&:hover"],
                            borderColor: "#e2e8f0",
                            backgroundColor: "#fafafa",
                          },
                        }}
                      >
                        <Box
                          component="img"
                          src={
                            item.product.image ||
                            item.product.main_image ||
                            "/placeholder-product.png"
                          }
                          alt={item.product.title}
                          sx={{
                            width: "100%",
                            height: "100%",
                            objectFit: "contain",
                          }}
                        />
                      </Box>

                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "flex-start",
                            gap: 1,
                          }}
                        >
                          <Typography
                            variant="body2"
                            component={RouterLink}
                            to={productHref}
                            sx={{
                              fontWeight: 600,
                              lineHeight: 1.3,
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                              ...productLinkSx,
                            }}
                          >
                            {item.product.title}
                          </Typography>
                          <Typography
                            variant="subtitle2"
                            sx={{
                              fontWeight: 700,
                              flexShrink: 0,
                              whiteSpace: "nowrap",
                            }}
                          >
                            <Price value={Number(item.product.price) + (item.shippingCost || 0)} tjsAmount={convertUsdToTjs(Number(item.product.price) + (item.shippingCost || 0), exchangeRate ?? null)} />
                          </Typography>
                        </Box>

                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            flexWrap: "wrap",
                            gap: 0.5,
                            mt: 0.5,
                          }}
                        >
                          {item.product.brand && (
                            <Typography
                              variant="caption"
                              className="notranslate"
                              translate="no"
                              sx={{ color: amazonColors.text.secondary }}
                            >
                              {item.product.brand}
                            </Typography>
                          )}
                          {item.product.condition && (
                            <Chip
                              label={item.product.condition}
                              size="small"
                              className="notranslate"
                              translate="no"
                              sx={{
                                bgcolor: "#F5F5F5",
                                px: 0.5,
                                height: 18,
                                borderRadius: 1,
                                color: amazonColors.text.secondary,
                                fontSize: "0.65rem",
                              }}
                            />
                          )}
                          {renderShippingInfo(item)}
                        </Box>

                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            mt: 1,
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              border: "1px solid #eee",
                              borderRadius: 50,
                              height: 28,
                              bgcolor: "#FAFAFA",
                            }}
                          >
                            <IconButton
                              size="small"
                              onClick={() =>
                                handleQuantityChange(
                                  item.cartItemId,
                                  item.quantity - 1
                                )
                              }
                              disabled={item.quantity <= 1}
                              sx={{ width: 28, height: 28 }}
                            >
                              <RemoveIcon sx={{ fontSize: 16 }} />
                            </IconButton>

                            <TextField
                              variant="standard"
                              value={item.quantity}
                              onChange={(e) => {
                                const value = parseInt(e.target.value);
                                if (!isNaN(value)) {
                                  handleQuantityChange(
                                    item.cartItemId,
                                    value
                                  );
                                }
                              }}
                              InputProps={{ disableUnderline: true }}
                              inputProps={{
                                min: 1,
                                max: 99,
                                inputMode: "numeric",
                                style: {
                                  textAlign: "center",
                                  width: 26,
                                  fontSize: 13,
                                  fontWeight: 600,
                                  padding: 0,
                                },
                              }}
                              sx={{ width: 36 }}
                            />

                            <IconButton
                              size="small"
                              onClick={() =>
                                handleQuantityChange(
                                  item.cartItemId,
                                  item.quantity + 1
                                )
                              }
                              disabled={item.quantity >= 99}
                              sx={{ width: 28, height: 28 }}
                            >
                              <AddIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Box>

                        <IconButton
                          size="small"
                          onClick={() =>
                            handleRemoveItem(item.cartItemId)
                          }
                            sx={{
                              color: amazonColors.text.secondary,
                              "&:hover": { color: amazonColors.red },
                            }}
                          >
                            <DeleteIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Box>
                      </Box>
                    </Box>
                    {index < items.length - 1 && <Divider />}
                  </Box>
                );
              })}
            </Card>
          </Box>

          {/* SUMMARY (non-scrollable column) */}
          <Box
            sx={{
              width: { xs: "100%", md: 360 },
              flexShrink: 0,
              display: { xs: "none", md: "block" },
            }}
          >
            <Box sx={{ position: "sticky", top: "var(--app-top-offset)" }}>
              {summaryCard}
            </Box>
          </Box>
        </Box>

      </Box>
    </Box>
  );
};

export default Cart;
